import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { formatCardDateTime, formatRelativeTime } from "@/lib/dates"
import { errorMessage } from "@/lib/errors"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusPill } from "@/components/status-pill"
import { cn } from "@/lib/utils"
import { Chip, TagChips } from "@/components/chips"
import { EntityMark } from "@/components/entity-mark"
import { formatSalary } from "@/lib/salary"
import { toast } from "sonner"

export const Route = createFileRoute("/candidate/applications")({
  component: SeekerApplications,
})

type ActivityItem = {
  key: string
  kind: "job" | "hackathon"
  title: string
  updatedAt: number
  status?: "applied" | "reviewed" | "shortlisted" | "rejected" | "withdrawn"
  jobId?: Id<"jobs">
  hackathonId?: Id<"hackathons">
  applicationId?: Id<"applications">
  canCancel: boolean
}

function SeekerApplications() {
  const apps = useQuery(api.applications.listMine)
  const regs = useQuery(api.hackathons.listMyRegistrations)
  const withdraw = useMutation(api.applications.withdraw)
  const unregister = useMutation(api.hackathons.unregister)
  const [openApplicationId, setOpenApplicationId] =
    useState<Id<"applications"> | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)

  const loading = apps === undefined || regs === undefined

  const items: ActivityItem[] = [
    ...(apps ?? []).map(
      (a): ActivityItem => ({
        key: a._id,
        kind: "job",
        title: a.jobTitle,
        updatedAt: a.updatedAt ?? a._creationTime,
        status: a.status,
        jobId: a.jobId,
        applicationId: a._id,
        canCancel: a.status !== "shortlisted" && a.status !== "withdrawn",
      }),
    ),
    ...(regs ?? []).map(
      (r): ActivityItem => ({
        key: r._id,
        kind: "hackathon",
        title: r.title,
        updatedAt: r._creationTime,
        hackathonId: r.hackathonId,
        canCancel: true,
      }),
    ),
  ].sort((a, b) => b.updatedAt - a.updatedAt)

  // hackathons.unregister / applications.withdraw both throw on a second call,
  // so an unguarded double click turns into a bogus "not registered" toast.
  const cancel = async (item: ActivityItem) => {
    if (cancelling) return
    setCancelling(item.key)
    try {
      if (item.kind === "job" && item.applicationId) {
        await withdraw({ applicationId: item.applicationId })
        toast.success("Application withdrawn")
      } else if (item.hackathonId) {
        await unregister({ hackathonId: item.hackathonId })
        toast.success("Registration cancelled")
      }
    } catch (e) {
      toast.error(errorMessage(e, "Could not cancel"))
    } finally {
      setCancelling(null)
    }
  }

  return (
    <div>
      <h1 className="mkt-page-title">My applications</h1>
      <p className="mkt-page-sub">
        Jobs and hackathons in one place, latest activity first.
      </p>
      <ul className="mt-6 list-none p-0">
        {items.map((item) => (
          <li key={item.key} className="mkt-page-row">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Chip emphasis={item.kind === "job"}>
                  {item.kind === "job" ? "Job" : "Hackathon"}
                </Chip>
                {item.kind === "job" && item.applicationId ? (
                  <button
                    type="button"
                    className="mkt-page-row-title truncate text-left"
                    onClick={() => setOpenApplicationId(item.applicationId!)}
                  >
                    {item.title}
                  </button>
                ) : item.hackathonId ? (
                  <Link
                    to="/hackathons/$hackathonId"
                    params={{ hackathonId: item.hackathonId }}
                    className="mkt-page-row-title truncate"
                  >
                    {item.title}
                  </Link>
                ) : (
                  <span className="mkt-page-row-title truncate">{item.title}</span>
                )}
                <span className="mkt-page-meta mt-0">
                  {formatRelativeTime(item.updatedAt)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {item.status && (
                  <StatusPill domain="application" status={item.status} />
                )}
                {item.kind === "job" && item.applicationId ? (
                  <button
                    type="button"
                    className="mkt-row-action"
                    onClick={() => setOpenApplicationId(item.applicationId!)}
                  >
                    Details
                  </button>
                ) : (
                  item.canCancel && (
                    <button
                      type="button"
                      className="mkt-row-action"
                      disabled={cancelling !== null}
                      onClick={() => void cancel(item)}
                    >
                      {cancelling === item.key ? "Cancelling…" : "Cancel"}
                    </button>
                  )
                )}
              </div>
            </div>
          </li>
        ))}
        {!loading && items.length === 0 && (
          <li className="py-3 text-[color:var(--wf-mute)]">
            Nothing yet —{" "}
            <Link
              to="/candidate"
              className="font-bold text-[color:var(--wf-lime-text)] hover:underline"
            >
              discover jobs and hackathons
            </Link>
            .
          </li>
        )}
      </ul>

      <ApplicationDetailSheet
        applicationId={openApplicationId}
        onOpenChange={(open) => {
          if (!open) setOpenApplicationId(null)
        }}
      />
    </div>
  )
}

const STATUS_WORDING: Record<string, { title: string; hint: string }> = {
  reviewed: { title: "Reviewed by the company", hint: "They've seen your profile" },
  shortlisted: {
    title: "Shortlisted",
    hint: "The company wants to move forward — expect a follow-up",
  },
  rejected: { title: "Not selected", hint: "Keep going — better fits are out there" },
}

function ApplicationDetailSheet({
  applicationId,
  onOpenChange,
}: {
  applicationId: Id<"applications"> | null
  onOpenChange: (open: boolean) => void
}) {
  const detail = useQuery(
    api.applications.getMineDetail,
    applicationId ? { applicationId } : "skip",
  )
  const withdraw = useMutation(api.applications.withdraw)
  const [withdrawing, setWithdrawing] = useState(false)

  const statusStep = detail?.status && detail.status !== "applied"
    ? STATUS_WORDING[detail.status]
    : null

  return (
    <Sheet open={applicationId !== null} onOpenChange={onOpenChange}>
      <SheetContent className="home-wf w-full overflow-y-auto bg-white text-[color:var(--wf-ink)] sm:max-w-lg">
        {detail === undefined ? (
          <div className="p-6">
            <Skeleton className="h-40 w-full" />
          </div>
        ) : detail === null ? (
          <div className="p-6 text-sm text-[color:var(--wf-mute)]">
            Application not found.
          </div>
        ) : (
          <>
            <SheetHeader>
              <span className="mkt-chip w-fit">Job application</span>
              <SheetTitle className="text-xl">
                {detail.job?.title ?? "Job"}
              </SheetTitle>
              {detail.job && (
                <SheetDescription>
                  {detail.job.employmentType} · {detail.job.location}
                  {formatSalary(detail.job) ? ` · ${formatSalary(detail.job)}` : ""}
                </SheetDescription>
              )}
            </SheetHeader>

            <div className="space-y-5 px-4 pb-6">
              {detail.company && (
                <div className="mkt-panel flex items-center gap-3 p-3 shadow-none">
                  <EntityMark
                    size="sm"
                    name={detail.company.companyName}
                    imageUrl={detail.company.companyLogoUrl}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {detail.company.companyName}
                    </p>
                    {detail.company.companyOneLiner && (
                      <p className="truncate text-xs text-[color:var(--wf-mute)]">
                        {detail.company.companyOneLiner}
                      </p>
                    )}
                  </div>
                  <Link
                    to="/companies/$companyId"
                    params={{ companyId: detail.company.companyId as string }}
                    className="home-wf-btn home-wf-btn--outline mkt-nav-join"
                  >
                    View
                  </Link>
                </div>
              )}

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--wf-mute)]">
                  Status
                </h3>
                <ol className="space-y-0">
                  <TimelineStep
                    done
                    title="Applied"
                    hint={formatCardDateTime(detail._creationTime)}
                  />
                  {statusStep ? (
                    <TimelineStep
                      done
                      last={detail.status !== "reviewed"}
                      title={statusStep.title}
                      hint={
                        detail.updatedAt
                          ? formatCardDateTime(detail.updatedAt)
                          : statusStep.hint
                      }
                    />
                  ) : (
                    <TimelineStep
                      title="Waiting on the company"
                      hint="You'll be notified when your application is reviewed"
                      last
                    />
                  )}
                  {detail.status === "reviewed" && (
                    <TimelineStep
                      title="Final decision"
                      hint="Waiting on the company"
                      last
                    />
                  )}
                </ol>
              </div>

              {detail.coverNote && (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--wf-mute)]">
                    Your cover note
                  </h3>
                  <p className="whitespace-pre-wrap rounded-lg bg-[color:var(--wf-soft)] p-3 text-sm">
                    {detail.coverNote}
                  </p>
                </div>
              )}

              {detail.job && (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--wf-mute)]">
                    The role
                  </h3>
                  <p className="line-clamp-6 whitespace-pre-wrap text-sm text-[color:var(--wf-ink)]/90">
                    {detail.job.description}
                  </p>
                  {detail.job.skills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <TagChips items={detail.job.skills} />
                    </div>
                  )}
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: detail.job._id }}
                    className="mt-1 inline-block font-bold text-[color:var(--wf-lime-text)] hover:underline"
                  >
                    View full posting →
                  </Link>
                </div>
              )}

              {detail.status !== "shortlisted" && (
                <>
                  <Separator />
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={withdrawing}
                    onClick={async () => {
                      if (withdrawing) return
                      setWithdrawing(true)
                      try {
                        await withdraw({ applicationId: detail._id })
                        toast.success("Application withdrawn")
                        onOpenChange(false)
                      } catch (e) {
                        toast.error(
                          errorMessage(e, "Could not withdraw"),
                        )
                      } finally {
                        setWithdrawing(false)
                      }
                    }}
                  >
                    {withdrawing ? "Withdrawing…" : "Withdraw application"}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function TimelineStep({
  title,
  hint,
  done,
  last,
}: {
  title: string
  hint: string
  done?: boolean
  last?: boolean
}) {
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {!last && (
        <span
          aria-hidden
          className="absolute left-[7px] top-5 h-full w-0.5 bg-[rgb(12_15_20/0.1)]"
        />
      )}
      <span
        aria-hidden
        className={cn(
          "relative z-10 mt-1 size-4 shrink-0 rounded-full border-2",
          done
            ? "border-[color:var(--wf-lime-text)] bg-[color:var(--wf-lime)]"
            : "border-[rgb(12_15_20/0.15)] bg-white",
        )}
      />
      <div>
        <p className={cn("text-sm font-medium", !done && "text-[color:var(--wf-mute)]")}>
          {title}
        </p>
        <p className="text-xs text-[color:var(--wf-mute)]">{hint}</p>
      </div>
    </li>
  )
}
